import { useState } from "react";
import { ArrowRightIcon } from "@sortera/react-icons";
import type { ProjectStage, SuggestionDto } from "../../types/index.ts";
import { Markdown } from "../../ui/markdown/index.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import styles from "./welcome.module.css";

const CONTAINER_EXAMPLE = `Jag vill kunna se alla containrar vi har utplacerade hos kunder.

En rad per container: vilken kund, vilken adress, vad den är till för — blandat avfall, trä, metall, gips — när vi satte ut den, och hur länge sedan den tömdes.

Det viktigaste: jag vill kunna filtrera fram de som stått över två veckor utan tömning. Det är de som kostar oss pengar.

Containrar som aldrig tömts ska synas som något annat än "tömdes för länge sedan" — det är olika problem.

Fyll den med påhittad data, ett tjugotal containrar, några kvarglömda.`;

function examplesFor(stage: ProjectStage): SuggestionDto[] {
  const examples: SuggestionDto[] = [
    { title: "Vad finns i projektet?", text: "Vad finns i projektet just nu, och vad skulle vara ett bra nästa steg?" },
    { title: "Bygg en första vy", text: CONTAINER_EXAMPLE },
  ];
  if (stage === "poc") {
    examples.push({ title: "Hur långt är det till MVP?", text: "Kan du göra en POC-granskning? Jag vill veta hur långt det är kvar till MVP." });
  } else {
    examples.push({ title: "Lägg upp det senaste", text: "Lägg upp det senaste i testmiljön och berätta var jag kan titta på det." });
  }
  return examples;
}

/**
 * Picking an example sends it as the first message — the conversation starts from it. With
 * `suggestions` (picked for this project from its README) they replace the fixed ones; while
 * they are on their way, a line says so.
 */
export function Welcome({
  projectName,
  stage,
  summary,
  suggestions,
  disabled,
  onPick,
}: {
  projectName: string;
  stage: ProjectStage;
  /** The project's own part of its README: what it is for and where it stands. */
  summary?: string | null;
  /** Undefined while they are fetched; null when there are none, and the fixed ones are used. */
  suggestions?: SuggestionDto[] | null;
  /** While the first message is on its way, so a second click can't start a second conversation. */
  disabled?: boolean;
  onPick: (text: string) => void;
}) {
  return (
    <section className={styles.welcome}>
      <p className="sc-kicker">Sortera Lab · {projectName}</p>
      <h1 className={`sc-heading ${styles.title}`}>Vad vill du bygga?</h1>
      <p className={styles.lead}>
        Beskriv det som du skulle beskriva det för en kollega. Tekniken behöver du inte nämna — den står redan i projektet.
      </p>

      {suggestions === undefined ? (
        <p className={styles.pending}>
          <Spinner /> Tar fram förslag för projektet …
        </p>
      ) : (
        <div className={styles.examples}>
          {(suggestions ?? examplesFor(stage)).map((example) => (
            <button
              key={example.title}
              type="button"
              className={styles.example}
              disabled={disabled}
              onClick={() => onPick(example.text)}
            >
              <span className={styles.exampleTitle}>{example.title}</span>
              <span className={styles.exampleText}>{example.text.split("\n")[0]}</span>
              <span className={styles.arrow} aria-hidden>
                <ArrowRightIcon size={16} />
              </span>
            </button>
          ))}
        </div>
      )}

      {summary ? (
        <About summary={summary} />
      ) : (
        <ul className={styles.tips}>
          <li>
            <strong>Namnge varje uppgift.</strong> Kund, adress, datum — det du utelämnar får Claude gissa.
          </li>
          <li>
            <strong>Ta med undantagen.</strong> Det som är självklart i verksamheten syns inte i en lista med fält.
          </li>
          <li>
            <strong>Säg varför.</strong> ”Det är de som kostar oss pengar” styr fler beslut än någon struktur.
          </li>
        </ul>
      )}
    </section>
  );
}

/**
 * What the README says about the project — a few lines, with the rest a click away. The ideas
 * above it are what the page is for; this is context.
 */
function About({ summary }: { summary: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={styles.about} aria-label="Om projektet">
      <p className="sc-kicker">Om projektet</p>
      <div className={open ? undefined : styles.clamped}>
        <Markdown>{summary}</Markdown>
      </div>
      <button type="button" className={styles.more} aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Visa mindre" : "Visa mer"}
      </button>
    </section>
  );
}
